export type ActionName =
  | "edit"
  | "delete"
  | "deactivate"
  | "resendEmail"
  | "reactivate";

export type ActionItem = {
  actionName: ActionName;
  actionLabel: string;
  actionFunction: () => void;
  disabled?: boolean;
  title?: string;
};

type SelectableUser = {
  canEdit?: boolean;
  canDelete?: boolean;
  canResendInvite?: boolean;
  canDeactivate?: boolean;
  canReactivate?: boolean;
};

type ActionDefinition = {
  actionName: ActionName;
  actionLabel: string;
  title: string;
  disabledTitle: string;
  flag: keyof SelectableUser;
  requiresSingleSelection?: boolean;
};

const ACTION_DEFINITIONS: ActionDefinition[] = [
  {
    actionName: "edit",
    actionLabel: "Edit",
    title: "Edit selected user",
    disabledTitle: "Select a single user you can edit",
    flag: "canEdit",
    requiresSingleSelection: true,
  },
  {
    actionName: "delete",
    actionLabel: "Delete",
    title: "Delete selected user(s)",
    disabledTitle: "Select only users you can delete",
    flag: "canDelete",
  },
  {
    actionName: "deactivate",
    actionLabel: "Deactivate User",
    title: "Deactivate selected user(s)",
    disabledTitle: "Select only users you can deactivate",
    flag: "canDeactivate",
  },
  {
    actionName: "resendEmail",
    actionLabel: "Resend Invite",
    title: "Resend invite to selected user(s)",
    disabledTitle: "Select only users you can resend an invite to",
    flag: "canResendInvite",
  },
  {
    actionName: "reactivate",
    actionLabel: "Reactivate User",
    title: "Reactivate selected user(s)",
    disabledTitle: "Select only users you can reactivate",
    flag: "canReactivate",
  },
];

const allAllow = (rows: SelectableUser[], flag: keyof SelectableUser) =>
  rows.length > 0 && rows.every((row) => Boolean(row[flag]));

export const buildActions = (
  selectedRows: SelectableUser[],
  onAction: (action: ActionName) => void,
): ActionItem[] =>
  ACTION_DEFINITIONS.map((definition) => {
    const disabled =
      (definition.requiresSingleSelection && selectedRows.length !== 1) ||
      !allAllow(selectedRows, definition.flag);

    return {
      actionName: definition.actionName,
      actionLabel: definition.actionLabel,
      title: disabled ? definition.disabledTitle : definition.title,
      disabled,
      actionFunction: () => {
        if (disabled) return;
        onAction(definition.actionName);
      },
    };
  });
